import { useEffect, useState } from "react";
import { useCurrentPlanLabel } from "../plan/useCurrentPlan";
import { useQuery } from "@tanstack/react-query";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { PanelLeft, PanelLeftClose } from "lucide-react";
import { brandingApi, brandingQueryKeys } from "../api/branding";
import { BillingStateBanner } from "./BillingStateBanner";
import { EmailVerifyBanner } from "./EmailVerifyBanner";
import { PlanRouteGuard } from "../screens/PlanRouteGuard";
import { isPlatformAdminFlag } from "../api/platform";
import { Sidebar } from "./Sidebar";
import { TopBar } from "./TopBar";
import { useResponsiveTableLabels } from "./useResponsiveTableLabels";
import { usePageScrollLock } from "./usePageScrollLock";
import { cn } from "@/lib/utils";

const COLLAPSED_W = 50; // px — narrow rail
const MIN_W = 200; // px — drag lower bound
const MAX_W = 400; // px — drag upper bound
const DEFAULT_W = 244; // px

const NAV_COLLAPSED_KEY = "addy.nav.collapsed";
const NAV_WIDTH_KEY = "addy.nav.width";

/** Placeholder "A" mark used on the collapsed rail until the real icon lands. */
function BrandMark({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        "flex items-center justify-center rounded-md bg-primary font-extrabold leading-none text-primary-foreground",
        className
      )}
      aria-hidden="true"
    >
      A
    </span>
  );
}

/**
 * Brand block at the top of the sidebar, aligned to the 50px top bar. Hosts the
 * logo, the plan badge (#19596 — was a hardcoded "Lite") and the collapse/expand control
 * (so the sidebar no longer
 * needs a "Thu gọn" footer). Collapsed, it shows the "A" rail mark and reveals
 * an expand button on hover.
 */
function BrandCell({
  collapsed,
  onToggle,
}: {
  collapsed: boolean;
  onToggle: () => void;
}) {
  const planBadge = useCurrentPlanLabel();
  const { data: branding } = useQuery({
    queryKey: brandingQueryKeys.all(),
    queryFn: () => brandingApi.get(),
    staleTime: 5 * 60 * 1000,
  });
  const clinicName = branding?.clinicName?.trim() || "Addy HIS";

  if (collapsed) {
    return (
      <div className="group relative flex h-[50px] shrink-0 items-center justify-center">
        {branding?.logoUrl ? (
          <img
            src={branding.logoUrl}
            alt=""
            className="h-8 w-8 rounded-md object-cover transition-opacity group-hover:opacity-0"
          />
        ) : (
          <BrandMark className="h-8 w-8 text-lg transition-opacity group-hover:opacity-0" />
        )}
        <button
          type="button"
          onClick={onToggle}
          title="Mở rộng menu"
          aria-label="Mở rộng menu"
          className="absolute inset-0 m-auto flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground group-hover:opacity-100"
        >
          <PanelLeft size={18} />
        </button>
      </div>
    );
  }

  return (
    <div className="flex h-[50px] shrink-0 items-center gap-2 px-2.5">
      {branding?.logoUrl ? (
        <img
          src={branding.logoUrl}
          alt=""
          className="h-8 w-8 shrink-0 rounded-md object-cover"
          title={clinicName}
        />
      ) : (
        <img src="/addy-logo.png" alt="Addy Care" className="h-7 w-auto shrink-0" title={clinicName} />
      )}
      {/*
        Huy hiệu gói. TRƯỚC #19596 đây là chuỗi viết cứng "Lite" — nó hiện "Lite" cho MỌI phòng
        khám trên MỌI màn, kể cả phòng khám đang trả gói Chuyên nghiệp, và nó cạnh logo nên nó là
        câu mà người dùng đọc nhiều nhất. Nay lấy từ gói thật; chưa biết thì KHÔNG hiện gì (quyết
        định của chủ sản phẩm 24/10: "chưa tải được gói, không mặc định hiển thị Lite").
      */}
      {planBadge && (
        <span className="shrink-0 rounded-full border border-primary/30 bg-primary/10 px-1.5 py-0.5 text-[10px] font-bold text-primary">
          {planBadge}
        </span>
      )}
      <button
        type="button"
        onClick={onToggle}
        title="Thu gọn menu"
        aria-label="Thu gọn menu"
        className="ml-auto flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      >
        <PanelLeftClose size={18} />
      </button>
    </div>
  );
}

export function AppShell() {
  const [navOpen, setNavOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(NAV_COLLAPSED_KEY) === "1"
  );
  const [width, setWidth] = useState(() => {
    const saved = Number(localStorage.getItem(NAV_WIDTH_KEY));
    if (!saved) return DEFAULT_W;
    return Math.min(MAX_W, Math.max(MIN_W, saved));
  });
  const location = useLocation();

  useResponsiveTableLabels(location.pathname);
  // Trang không cuộn khi khung ứng dụng đang mở — xem `usePageScrollLock` (#19629).
  usePageScrollLock();

  // Close the mobile drawer whenever the route changes.
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  // Lock body scroll while the drawer is open on mobile.
  useEffect(() => {
    document.body.style.overflow = navOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [navOpen]);

  // Persist the resized width.
  useEffect(() => {
    localStorage.setItem(NAV_WIDTH_KEY, String(width));
  }, [width]);

  const toggleCollapsed = () =>
    setCollapsed((v) => {
      const next = !v;
      localStorage.setItem(NAV_COLLAPSED_KEY, next ? "1" : "0");
      return next;
    });

  const [resizing, setResizing] = useState(false);

  // Drag the right edge to resize the sidebar (desktop, expanded only).
  const startResize = (e: React.MouseEvent) => {
    e.preventDefault();
    setResizing(true);
    const onMove = (ev: MouseEvent) => {
      setWidth(Math.min(MAX_W, Math.max(MIN_W, ev.clientX)));
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
      setResizing(false);
    };
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const sidebarWidth = collapsed ? COLLAPSED_W : width;

  /*
   * Tài khoản nền tảng KHÔNG có dữ liệu phòng khám, nên khung phòng khám không được render cho
   * nó (#19805). `PlatformShell` đã chặn chiều ngược lại từ lâu — người không phải platform vào
   * `/platform/*` thì bị đẩy về `/dashboard` — nhưng chiều này bỏ trống, nên `platform_superadmin`
   * mở `/` (redirect sang `/dashboard`), bookmark, hay F5 là vào thẳng dashboard phòng khám.
   *
   * <p>Hậu quả đo được 19/10: 5 đợt trong buổi sáng, mỗi đợt 10–20 request 403 cùng lúc
   * (`/appointments`, `/branches`, `/clinic/features`, `/clinic/branding`,
   * `/reports/revenue/daily`, `/invoices`, `/invoices/totals`). API từ chối là ĐÚNG; cái sai là
   * web đi gọi. Người dùng thấy dashboard trống, và log bị nhiễu tới mức khó thấy 403 thật.
   *
   * <p>Đặt SAU các hook của component này (rules-of-hooks: không return trước hook) nhưng TRƯỚC
   * khi render — vậy là đủ, vì thứ gọi API không phải hook của `AppShell` mà là `BrandCell`
   * (`brandingApi.get()`) và các trang con tự fetch khi mount; chúng chỉ mount nếu cây này được
   * render. Hook của `AppShell` chỉ chạm DOM/localStorage.
   *
   * <p>Đây là sửa ĐIỀU HƯỚNG, không phải sửa phân quyền: chốt cửa thật vẫn là backend
   * (permission + assertion realm PLATFORM), và nó đã làm đúng việc của nó.
   */
  if (isPlatformAdminFlag()) {
    return <Navigate to="/platform/tenants" replace />;
  }


  return (
    <div
      className="flex h-screen overflow-hidden bg-background"
      style={{ ["--sw" as string]: `${sidebarWidth}px` }}
    >
      {/* Sidebar column (desktop) / slide-in drawer (mobile) */}
      <aside
        className={cn(
          "relative z-[60] flex w-[264px] max-w-[82vw] flex-col border-r border-border bg-card transition-transform duration-200",
          "max-md:fixed max-md:inset-y-0 max-md:left-0 max-md:shadow-2xl",
          navOpen ? "max-md:translate-x-0" : "max-md:-translate-x-full",
          "md:static md:w-[var(--sw)] md:max-w-none md:translate-x-0 md:shadow-none",
          resizing ? "md:transition-none" : "md:transition-[width]"
        )}
      >
        <BrandCell collapsed={collapsed} onToggle={toggleCollapsed} />
        <Sidebar collapsed={collapsed} onNavigate={() => setNavOpen(false)} />

        {/* Drag handle (desktop, expanded only) */}
        {!collapsed && (
          <div
            role="separator"
            aria-orientation="vertical"
            onMouseDown={startResize}
            title="Kéo để đổi độ rộng"
            className="absolute inset-y-0 right-0 hidden w-1.5 cursor-col-resize hover:bg-primary/30 md:block"
          />
        )}
      </aside>

      {/* Backdrop (mobile only, when drawer open) */}
      {navOpen && (
        <button
          type="button"
          aria-label="Đóng menu"
          onClick={() => setNavOpen(false)}
          className="fixed inset-0 z-50 bg-black/40 md:hidden"
        />
      )}

      {/* Content column */}
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar onMenuToggle={() => setNavOpen((v) => !v)} navOpen={navOpen} />
        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          <EmailVerifyBanner />
          <BillingStateBanner />
          <PlanRouteGuard>
            <Outlet />
          </PlanRouteGuard>
        </main>
      </div>
    </div>
  );
}
